import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import ContactForm41 from '../components/contact-form41'
import RecentBlogPosts from '../components/recent-blog-posts'
import Footer from '../components/footer'
import IntegrationsNav from '../components/integrations-nav'
import { getCanonicalUrl, getCurrentPath } from '../utils/canonical'
import { trackSignUpConversion, trackGetStartedClick } from '../utils/googleAds'
import MagicBento, { ParticleCard, GlobalSpotlight } from '../components/MagicBento'
import './home-v2.css'

const useCountUp = (target, duration = 1800, decimals = 0) => {
  const [value, setValue] = useState(0)
  const ref = useRef(null)
  const started = useRef(false)

  const animate = useCallback(() => {
    const startTime = performance.now()
    const tick = (now) => {
      const elapsed = now - startTime
      const progress = Math.min(elapsed / duration, 1)
      const ease = 1 - Math.pow(1 - progress, 3)
      setValue(parseFloat((ease * target).toFixed(decimals)))
      if (progress < 1) requestAnimationFrame(tick)
      else setValue(target)
    }
    requestAnimationFrame(tick)
  }, [target, duration, decimals])

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true
        animate()
        observer.disconnect()
      }
    }, { threshold: 0.3 })
    observer.observe(el)
    return () => observer.disconnect()
  }, [animate])

  return [value, ref]
}

const autoReplies = [
  "Thanks for reaching out! A DataFlows specialist will be in touch shortly.",
  "Message received ✓ — your SMS was delivered instantly.",
  "Got it! DataFlows makes it this easy for your customers too.",
  "Your campaign just reached 10,000 customers in under 3 seconds! 🚀",
  "OTP delivered. Customer verified successfully.",
]

const initialMessages = [
  { id: 1, text: 'Hi! Welcome to DataFlows 👋 Your account is set up and ready.', from: 'df', time: '9:41 AM' },
  { id: 2, text: 'You have 1,000 SMS credits loaded. Start sending to your customers today.', from: 'df', time: '9:41 AM' },
  { id: 3, text: 'Reply anytime — two-way messaging is built in.', from: 'df', time: '9:42 AM' },
]

const PhoneMockup = () => {
  const [messages, setMessages] = useState([])
  const [hasAnimated, setHasAnimated] = useState(false)
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [charCount, setCharCount] = useState(0)
  const messagesContainerRef = useRef(null)
  const phoneSectionRef = useRef(null)

  // Scroll only inside the phone container — never the page
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, isTyping])

  // Reveal messages one-by-one when phone scrolls into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true)
          initialMessages.forEach((msg, i) => {
            setTimeout(() => {
              setMessages(prev => [...prev, msg])
            }, i * 900)
          })
        }
      },
      { threshold: 0.35 }
    )
    if (phoneSectionRef.current) observer.observe(phoneSectionRef.current)
    return () => observer.disconnect()
  }, [hasAnimated])

  const sendMessage = () => {
    if (!input.trim()) return
    const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    setMessages(prev => [...prev, { id: Date.now(), text: input.trim(), from: 'user', time: now }])
    setInput('')
    setCharCount(0)
    setIsTyping(true)
    setTimeout(() => {
      setIsTyping(false)
      const reply = autoReplies[Math.floor(Math.random() * autoReplies.length)]
      setMessages(prev => [...prev, { id: Date.now() + 1, text: reply, from: 'df', time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }])
    }, 1400)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage() }
  }

  const handleChange = (e) => {
    setInput(e.target.value)
    setCharCount(e.target.value.length)
  }

  return (
    <div className="v2-phone-section" ref={phoneSectionRef}>
      <div className="v2-phone-side-text" data-aos="fade-right">
        <span className="v2-section-label">Try it live</span>
        <h2>See SMS in action</h2>
        <p>Type a message and hit send — experience the speed and simplicity of DataFlows SMS firsthand. This is exactly what your customers see.</p>
        <ul className="v2-phone-feature-list">
          <li>
            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
            160 characters per SMS
          </li>
          <li>
            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
            Instant two-way messaging
          </li>
          <li>
            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
            Branded sender ID (your business name)
          </li>
          <li>
            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
            99.9% delivery rate across Australia
          </li>
        </ul>
        <a href="https://sms.dataflows.com.au/register" target="_blank" rel="noreferrer noopener" className="v2-btn-primary">Start for free</a>
      </div>

      <div className="v2-phone-frame" data-aos="fade-left">
        <div className="v2-phone-notch"><div className="v2-phone-notch-pill" /></div>

        <div className="v2-phone-screen">
          <div className="v2-phone-status-bar">
            <span>9:41</span>
            <div className="v2-phone-status-icons">
              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>
              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z"/></svg>
            </div>
          </div>

          <div className="v2-phone-chat-header">
            <div className="v2-phone-avatar">DF</div>
            <div className="v2-phone-contact-info">
              <div className="v2-phone-contact-name">DataFlows</div>
              <div className="v2-phone-contact-status">
                <span className="v2-online-dot" />
                SMS Platform
              </div>
            </div>
          </div>

          <div className="v2-phone-messages" ref={messagesContainerRef}>
            <div className="v2-phone-date-divider">Today</div>
            {messages.map(msg => (
              <div key={msg.id} className={`v2-msg v2-msg--${msg.from} v2-msg--in`}>
                <div className="v2-msg-bubble">{msg.text}</div>
                <div className="v2-msg-time">{msg.time}</div>
              </div>
            ))}
            {isTyping && (
              <div className="v2-msg v2-msg--df v2-msg--in">
                <div className="v2-msg-bubble v2-typing-bubble">
                  <span className="v2-dot" /><span className="v2-dot" /><span className="v2-dot" />
                </div>
              </div>
            )}
          </div>

          <div className="v2-phone-input-bar">
            <input
              type="text"
              value={input}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              maxLength={160}
            />
            <span className="v2-char-count">{charCount}/160</span>
            <button
              className={`v2-send-btn${input.trim() ? ' v2-send-btn--active' : ''}`}
              onClick={sendMessage}
              aria-label="Send"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
            </button>
          </div>
        </div>

        <div className="v2-phone-home-indicator" />
      </div>
    </div>
  )
}

const HomeV2 = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768)
  const featuresGridRef = useRef(null)

  const [delivery, deliveryRef] = useCountUp(99.9, 1800, 1)
  const [openRate, openRateRef] = useCountUp(98, 1600, 0)
  const [smsPrice, smsPriceRef] = useCountUp(2.5, 1400, 1)

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const handleGetStartedClick = () => {
    trackGetStartedClick()
    trackSignUpConversion()
  }

  useEffect(() => {
    if (typeof AOS !== 'undefined') {
      AOS.init({ duration: 700, easing: 'ease-out', once: true, offset: 80 })
    }
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
      setShowScrollTop(window.scrollY > 400)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' })

  const features = [
    {
      icon: '/logos/icons/bulk-sms.png',
      fallbackIcon: 'https://cdn-icons-png.flaticon.com/512/1323/1323772.png',
      title: 'Bulk SMS',
      desc: 'Deliver messages to large audiences instantly. Reach thousands of customers simultaneously with personalised messages for promotions, alerts, and announcements.',
    },
    {
      icon: '/logos/icons/api.png',
      fallbackIcon: 'https://cdn-icons-png.flaticon.com/512/17034/17034231.png',
      title: 'SMS API',
      desc: 'Integrate SMS into your existing systems with our robust REST API. Full webhooks, SDKs for popular languages, and comprehensive documentation included.',
    },
    {
      icon: '/logos/icons/branded.png',
      fallbackIcon: 'https://cdn-icons-png.flaticon.com/512/7111/7111069.png',
      title: 'Branded SMS',
      desc: 'Send messages with your business name instead of a generic number. Up to 45% higher engagement compared to unknown numbers.',
    },
    {
      icon: '/logos/icons/twoway.png',
      fallbackIcon: 'https://cdn-icons-png.flaticon.com/512/14469/14469425.png',
      title: 'Two-Way Messaging',
      desc: 'Enable real conversations with your customers. Perfect for support, appointment confirmations, feedback, and surveys with intelligent routing.',
    },
    {
      icon: '/logos/icons/otp.png',
      fallbackIcon: 'https://cdn-icons-png.flaticon.com/512/6357/6357048.png',
      title: 'OTP / Verification',
      desc: 'Enhance security with one-time passwords. Customisable templates, expiration times, and retry logic for robust authentication.',
    },
    {
      icon: '/logos/icons/automation.png',
      fallbackIcon: 'https://cdn-icons-png.flaticon.com/512/10492/10492948.png',
      title: 'Automation',
      desc: 'Automate birthday wishes, reminders, and drip campaigns. Trigger-based workflows send perfectly timed messages without manual effort.',
    },
    {
      icon: '/logos/icons/dashboard.png',
      fallbackIcon: 'https://cdn-icons-png.flaticon.com/512/8899/8899687.png',
      title: 'Dashboard',
      desc: 'Manage all your messaging activities from one intuitive dashboard. Monitor campaigns, contacts, delivery rates, and spending at a glance.',
    },
    {
      icon: '/logos/icons/campaigns.png',
      fallbackIcon: 'https://cdn-icons-png.flaticon.com/512/18382/18382499.png',
      title: 'Campaigns',
      desc: 'Launch targeted campaigns with precision. Create, execute, and monitor your marketing initiatives seamlessly from a single platform.',
    },
    {
      icon: '/logos/icons/templates.png',
      fallbackIcon: 'https://cdn-icons-png.flaticon.com/512/1085/1085728.png',
      title: 'Templates',
      desc: 'Design reusable templates for every messaging need. From promotional messages to reminders, save time and maintain consistency.',
    },
    {
      icon: '/logos/icons/schedule.png',
      fallbackIcon: 'https://cdn-icons-png.flaticon.com/512/6752/6752827.png',
      title: 'Scheduled Campaigns',
      desc: 'Plan campaigns ahead of time and reach your audience at the optimal moment. Align with customer time zones for maximum impact.',
    },
    {
      icon: '/logos/icons/blacklist.png',
      fallbackIcon: 'https://cdn-icons-png.flaticon.com/512/3206/3206886.png',
      title: 'Blacklist Management',
      desc: 'Block unwanted numbers and manage opt-outs automatically. Ensure your messages always reach the right people.',
    },
    {
      icon: '/logos/icons/analytics.png',
      fallbackIcon: 'https://cdn-icons-png.flaticon.com/512/11083/11083341.png',
      title: 'Analysis & Reporting',
      desc: 'Gain deep insights with our analytics tools. Understand your audience, track delivery rates, and refine your messaging strategy.',
    },
  ]

  const pricingPlans = [
    {
      name: 'Mini',
      price: '$35',
      period: 'AUD',
      rate: '3.5¢ / SMS',
      highlight: false,
      badge: null,
      billingNote: 'Pay-as-you-go — no monthly or annual fees. Top up only when you need more credits.',
      features: ['1,000 SMS credits', '30 Contact Lists', '30 SMS Templates', 'Zero Subscription Fees'],
      cta: 'Get Started',
      ctaHref: 'https://sms.dataflows.com.au/register',
    },
    {
      name: 'Pro',
      price: '$150',
      period: 'AUD',
      rate: '3¢ / SMS',
      highlight: false,
      badge: null,
      billingNote: 'Pay-as-you-go — no monthly or annual fees. Top up only when you need more credits.',
      features: ['5,000 SMS credits', 'Free Sender ID (Business Name)', '50 Contact Lists', '50 SMS Templates', 'API Integrations', 'Zero Subscription Fees'],
      cta: 'Get Started',
      ctaHref: 'https://sms.dataflows.com.au/register',
    },
    {
      name: 'Business',
      price: '$500',
      period: 'AUD',
      rate: '2.5¢ / SMS',
      highlight: true,
      badge: 'Best Value',
      billingNote: 'Pay-as-you-go — no monthly or annual fees. Top up only when you need more credits.',
      features: ['20,000 SMS credits', 'Free Sender ID (Business Name)', 'Unlimited Contact Lists', 'Unlimited Templates', 'API Integrations', 'Zero Subscription Fees'],
      cta: 'Get Started',
      ctaHref: 'https://sms.dataflows.com.au/register',
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      rate: 'Volume pricing',
      highlight: false,
      badge: null,
      billingNote: 'Custom volume pricing — let’s talk about terms that fit your business.',
      features: ['All Features Included', 'Unbeatable Price for High-Volume', 'Dedicated Account Manager', 'SLA Guarantee', 'Custom Integrations', '24/7 Priority Support'],
      cta: 'Contact Us',
      ctaHref: '#ContactForm41',
    },
  ]

  const integrationLogos = [
    { src: '/logos/icons/teams.png', alt: 'Microsoft Teams', label: 'Microsoft Teams', path: '/integrations/microsoft-teams' },
    { src: 'https://cdn.simpleicons.org/zapier', alt: 'Zapier', label: 'Zapier', path: '/integrations/zapier' },
    { src: '/logos/icons/power-automate.png', alt: 'Power Automate', label: 'Power Automate', path: '/integrations/power-automate' },
    { src: 'https://cdn.simpleicons.org/supabase', alt: 'Supabase', label: 'Supabase', path: '/integrations/supabase' },
    { src: 'https://cdn.simpleicons.org/auth0', alt: 'Auth0', label: 'Auth0', path: '/integrations/auth0' },
    { src: 'https://cdn.simpleicons.org/shopify', alt: 'Shopify', label: 'Shopify', path: '/integrations/shopify' },
    { src: 'https://cdn.simpleicons.org/wordpress', alt: 'WordPress', label: 'WordPress', path: '/integrations/wordpress' },
    { src: '/logos/icons/gohighlevel.png', alt: 'GoHighLevel', label: 'GoHighLevel', path: '/integrations/gohighlevel' },
    { src: '/logos/icons/cliniko.png', alt: 'Cliniko', label: 'Cliniko', path: '/integrations/cliniko' },
    { src: 'https://cdn.simpleicons.org/hubspot', alt: 'HubSpot', label: 'HubSpot', path: 'https://zapier.com/apps/dataflows-sms/integrations/hubspot', external: true },
    { src: 'https://cdn.simpleicons.org/mailchimp', alt: 'Mailchimp', label: 'Mailchimp', path: 'https://zapier.com/apps/dataflows-sms/integrations/mailchimp', external: true },
    { src: 'https://cdn.simpleicons.org/xero', alt: 'Xero', label: 'Xero', path: 'https://zapier.com/apps/dataflows-sms/integrations/xero', external: true },
    { src: 'https://cdn.simpleicons.org/stripe', alt: 'Stripe', label: 'Stripe', path: 'https://zapier.com/apps/dataflows-sms/integrations/stripe', external: true },
  ]

  return (
    <div className="v2-root">
      <Helmet>
        <title>SMS Gateway & API Platform for Australian Business | DataFlows</title>
        <link rel="canonical" href="https://dataflows.com.au/" />
        <meta name="description" content="Leading SMS platform for Australian businesses. Reliable SMS API, bulk messaging & automation. ACMA compliant. From 2.5¢ per SMS." />
        <meta name="keywords" content="SMS for business Australia, SMS API Australia, bulk SMS Australia, business SMS marketing, Australian SMS service, SMS platform Australia" />
        <meta property="og:title" content="SMS Gateway & API Platform for Australian Business | DataFlows" />
        <meta property="og:description" content="Leading SMS for business Australia platform with reliable SMS API, bulk messaging, and automated SMS marketing." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "DataFlows SMS Platform",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "offers": [
            {"@type": "Offer", "name": "Mini", "price": "35.00", "priceCurrency": "AUD", "description": "One-time SMS credit pack, pay-as-you-go with no recurring charges."},
            {"@type": "Offer", "name": "Pro", "price": "150.00", "priceCurrency": "AUD", "description": "One-time SMS credit pack, pay-as-you-go with no recurring charges."},
            {"@type": "Offer", "name": "Business", "price": "500.00", "priceCurrency": "AUD", "description": "One-time SMS credit pack, pay-as-you-go with no recurring charges."}
          ],
          "description": "ACMA-registered SMS marketing platform for Australian businesses. Bulk SMS, branded sender IDs, SMS API, Zapier and Power Automate integrations.",
          "url": "https://dataflows.com.au",
          "provider": {
            "@type": "Organization",
            "name": "DataFlows",
            "sameAs": "https://dataflows.com.au"
          }
        })}</script>
      </Helmet>

      {/* ── NAVBAR ── */}
      <nav className={`v2-nav${scrolled ? ' v2-nav--scrolled' : ''}${mobileMenuOpen ? ' v2-nav--menu-open' : ''}`}>
        <div className="v2-nav-inner">
          <Link to="/" className="v2-nav-logo">
            <img src="/dataflows-high-resolution-logo-200h.png" alt="DataFlows" height="36" />
          </Link>

          <ul className={`v2-nav-links${mobileMenuOpen ? ' v2-nav-links--open' : ''}`}>
            <li><a href="#features" className="v2-nav-link" onClick={() => setMobileMenuOpen(false)}>Features</a></li>
            <li><a href="#how-it-works" className="v2-nav-link" onClick={() => setMobileMenuOpen(false)}>How it works</a></li>
            <li><a href="#pricing" className="v2-nav-link" onClick={() => setMobileMenuOpen(false)}>Pricing</a></li>
            <li><Link to="/blog" className="v2-nav-link" onClick={() => setMobileMenuOpen(false)}>Blog</Link></li>
            <li className="v2-nav-integrations-wrapper"><IntegrationsNav /></li>
            <li><Link to="/developers" className="v2-nav-link" onClick={() => setMobileMenuOpen(false)}>Developers</Link></li>
            <li><Link to="/contact-us" className="v2-nav-link" onClick={() => setMobileMenuOpen(false)}>Contact Us</Link></li>
          </ul>

          <div className="v2-nav-actions">
            <a href="https://sms.dataflows.com.au" target="_blank" rel="noreferrer noopener" className="v2-btn-ghost v2-nav-signin">Sign in</a>
            <a href="https://sms.dataflows.com.au/register" target="_blank" rel="noreferrer noopener" className="v2-btn-primary v2-nav-cta" onClick={handleGetStartedClick}>Get started</a>
          </div>

          <button className="v2-hamburger" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
            {mobileMenuOpen
              ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12h18M3 6h18M3 18h18"/></svg>
            }
          </button>
        </div>

      </nav>

      {/* Mobile menu — outside nav so fixed positioning is relative to viewport */}
      {mobileMenuOpen && (
        <div className="v2-mobile-menu">
          <a href="#features" onClick={() => setMobileMenuOpen(false)}>Features</a>
          <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)}>How it works</a>
          <a href="#pricing" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
          <Link to="/blog" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
          <Link to="/integrations" onClick={() => setMobileMenuOpen(false)}>Integrations</Link>
          <Link to="/developers" onClick={() => setMobileMenuOpen(false)}>Developers</Link>
          <Link to="/contact-us" onClick={() => setMobileMenuOpen(false)}>Contact Us</Link>
          <div className="v2-mobile-menu-actions">
            <a href="https://sms.dataflows.com.au" target="_blank" rel="noreferrer noopener" className="v2-btn-ghost">Sign in</a>
            <a href="https://sms.dataflows.com.au/register" target="_blank" rel="noreferrer noopener" className="v2-btn-primary" onClick={handleGetStartedClick}>Get started</a>
          </div>
        </div>
      )}

      {/* ── HERO ── */}
      <section className="v2-hero">
        <div className="v2-hero-glow" />
        <div className="v2-hero-content" data-aos="fade-up">
          <div className="v2-hero-badge">
            <span className="v2-hero-badge-dot" />
            Australia's #1 SMS Platform
          </div>
          <div className="v2-hero-trust-badges">
            <a href="https://www.acma.gov.au/approved-telcos-and-message-providers-sms-sender-id-register" target="_blank" rel="noreferrer noopener" className="v2-trust-badge v2-trust-badge--acma">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
              ACMA Registered Originating Telco
            </a>
            <span className="v2-trust-badge">✓ Spam Act 2003</span>
            <span className="v2-trust-badge">✓ No lock-in contracts</span>
          </div>
          <h1 className="v2-hero-title">
            The SMS tool your<br />
            <span className="v2-gradient-text">business actually needs</span>
          </h1>
          <p className="v2-hero-subtitle">
            Reliable business messaging, built for Australia.
            Connect with your customers instantly using a platform designed for speed, scale, and compliance.
          </p>
          <div className="v2-hero-actions">
            <a href="https://sms.dataflows.com.au/register" target="_blank" rel="noreferrer noopener" className="v2-btn-primary v2-btn-lg" onClick={handleGetStartedClick}>
              Get started free
              <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18"><path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
            </a>
            <a href="#pricing" className="v2-btn-outline v2-btn-lg">View plans</a>
          </div>
          <p className="v2-hero-no-cc">No credit card required · Cancel anytime</p>
          <div className="v2-hero-stats" ref={deliveryRef}>
            <div className="v2-stat"><span className="v2-stat-num">{delivery}%</span><span className="v2-stat-label">Delivery rate</span></div>
            <div className="v2-stat-divider" />
            <div className="v2-stat"><span className="v2-stat-num" ref={openRateRef}>{openRate}%</span><span className="v2-stat-label">Open rate</span></div>
            <div className="v2-stat-divider" />
            <div className="v2-stat"><span className="v2-stat-num" ref={smsPriceRef}>{smsPrice}¢</span><span className="v2-stat-label">From per SMS</span></div>
          </div>
        </div>
        <div className="v2-hero-visual" data-aos="fade-left" data-aos-delay="150">
          <div className="v2-spline-wrapper">
            <spline-viewer
              url="https://prod.spline.design/DlnNMurvV4Ugn-k6/scene.splinecode"
              style={{ '--spline-branding-display': 'none' }}
            />
          </div>
        </div>
      </section>

      {/* ── DASHBOARD PREVIEW ── */}
      <div className="v2-dashboard-preview" data-aos="zoom-in" data-aos-delay="100">
        <div className="v2-dashboard-frame">
          <div className="v2-dashboard-bar">
            <span /><span /><span />
          </div>
          <img src="/logos/4-1400w.png" alt="DataFlows Dashboard" loading="lazy" />
        </div>
      </div>

      {/* ── MAGIC BENTO ── */}
      <section className="v2-section v2-bento-section">
        <div className="v2-section-inner">
          <div className="v2-section-header" data-aos="fade-up">
            <span className="v2-section-label">Platform</span>
            <h2 className="v2-section-title">Everything you need to SMS at scale</h2>
            <p className="v2-section-subtitle">One platform, all the tools — built for Australian businesses that take messaging seriously.</p>
          </div>
          <div data-aos="fade-up" data-aos-delay="100">
            <MagicBento
              textAutoHide={true}
              enableStars={true}
              enableSpotlight={true}
              enableBorderGlow={true}
              enableTilt={false}
              enableMagnetism={false}
              clickEffect={true}
              spotlightRadius={400}
              particleCount={12}
              glowColor="82, 40, 245"
              disableAnimations={false}
            />
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" className="v2-section">
        <div className="v2-section-inner">
          <div className="v2-section-header" data-aos="fade-up">
            <span className="v2-section-label">Features</span>
            <h2 className="v2-section-title">Effortless Messaging</h2>
            <p className="v2-section-subtitle">Expert service, unbeatable prices — messaging made simple. Our range of solutions helps businesses of all sizes connect with customers effectively.</p>
          </div>
          <GlobalSpotlight
            gridRef={featuresGridRef}
            enabled={!isMobile}
            spotlightRadius={350}
            glowColor="82, 40, 245"
          />
          <div className="v2-features-grid mb-bento-section" ref={featuresGridRef}>
            {features.map((f, i) => (
              <ParticleCard
                key={i}
                className="v2-feature-card mb-card mb-card--glow"
                style={{ '--glow-color': '82, 40, 245' }}
                glowColor="82, 40, 245"
                particleCount={8}
                clickEffect={true}
                disableAnimations={isMobile}
              >
                <div className="v2-feature-icon">
                  <img
                    src={f.fallbackIcon}
                    alt={f.title}
                    width="32"
                    height="32"
                    onError={e => { e.target.src = f.fallbackIcon }}
                  />
                </div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </ParticleCard>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIAL ── */}
      <section className="v2-testimonial-section" data-aos="fade-up">
        <div className="v2-testimonial">
          <div className="v2-testimonial-quote">"</div>
          <p className="v2-testimonial-text">
            Our SMS campaigns have transformed our customer engagement — simple, effective, and reliable. Sending messages with our brand name has boosted our credibility and customer trust.
          </p>
          <div className="v2-testimonial-author">
            <img
              src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face"
              alt="Natalie Furrow"
              className="v2-testimonial-avatar"
            />
            <div>
              <div className="v2-testimonial-name">Natalie Furrow</div>
              <div className="v2-testimonial-role">Founder and CEO</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTENT MANAGEMENT BANNER ── */}
      <section className="v2-banner v2-banner--alt" data-aos="fade-up">
        <div className="v2-banner-inner">
          <div className="v2-banner-text" data-aos="fade-right" data-aos-duration="1000">
            <span className="v2-section-label">Content Management</span>
            <h2>Reach all your customers in just one place</h2>
            <p>Manage all your customer communications effortlessly from a single platform. Whether you're sending promotions, reminders, or updates, our service lets you reach your entire audience in one streamlined place, saving you time and ensuring consistent messaging.</p>
            <a href="https://sms.dataflows.com.au/register" target="_blank" rel="noreferrer noopener" className="v2-btn-primary" onClick={handleGetStartedClick}>Get started</a>
          </div>
          <div className="v2-banner-image" data-aos="fade-left" data-aos-duration="1000">
            <img src="/logos/brand%20loyalty-pana-1200w.png" alt="Brand Loyalty" loading="lazy" />
          </div>
        </div>
      </section>

      {/* ── SMS POWER FEATURES BANNER ── */}
      <section className="v2-banner" data-aos="fade-up">
        <div className="v2-banner-inner v2-banner-inner--reverse">
          <div className="v2-banner-image" data-aos="fade-right" data-aos-duration="1000">
            <img src="/data%20report-pana-1200w.png" alt="Data Report" loading="lazy" />
          </div>
          <div className="v2-banner-text" data-aos="fade-left" data-aos-duration="1000">
            <span className="v2-section-label">Features</span>
            <h2>SMS Power Features: Scale, Track, and Personalise</h2>
            <div className="v2-feature-list">
              <div className="v2-feature-list-item">
                <div className="v2-feature-list-dot" />
                <div>
                  <strong>Scalability</strong>
                  <span>Handle large volumes of SMS with ease</span>
                </div>
              </div>
              <div className="v2-feature-list-item">
                <div className="v2-feature-list-dot" />
                <div>
                  <strong>Delivery Reports</strong>
                  <span>Track message delivery in real-time</span>
                </div>
              </div>
              <div className="v2-feature-list-item">
                <div className="v2-feature-list-dot" />
                <div>
                  <strong>Personalisation</strong>
                  <span>Customise messages for different customer segments</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CAMPAIGNS BANNER ── */}
      <section className="v2-banner v2-banner--alt" data-aos="fade-up">
        <div className="v2-banner-inner">
          <div className="v2-banner-text" data-aos="fade-right" data-aos-duration="1000">
            <span className="v2-section-label">Use Cases</span>
            <h2>Supercharge Your Sales with Precision SMS Campaigns</h2>
            <p>Transform your marketing strategy with targeted SMS campaigns designed to engage your audience and drive conversions. Segment your audience and craft personalised messages that resonate with each group, ensuring maximum impact and increased sales.</p>
            <a href="https://sms.dataflows.com.au/register" target="_blank" rel="noreferrer noopener" className="v2-btn-primary" onClick={handleGetStartedClick}>Get started</a>
          </div>
          <div className="v2-banner-image" data-aos="fade-left" data-aos-duration="1000">
            <img src="/logos/messages-amico-1200w.png" alt="SMS Campaigns" loading="lazy" />
          </div>
        </div>
      </section>

      {/* ── SCHEDULED MESSAGING BANNER ── */}
      <section className="v2-banner" data-aos="fade-up">
        <div className="v2-banner-inner v2-banner-inner--reverse">
          <div className="v2-banner-image" data-aos="fade-right" data-aos-duration="1000">
            <img src="/schedule-amico-1200w.png" alt="Scheduled Messaging" loading="lazy" />
          </div>
          <div className="v2-banner-text" data-aos="fade-left" data-aos-duration="1000">
            <span className="v2-section-label">Use Cases</span>
            <h2>Scheduled Messaging: Send When It Matters Most</h2>
            <div className="v2-feature-list">
              <div className="v2-feature-list-item">
                <div className="v2-feature-list-dot" />
                <div>
                  <strong>Flexible Timing</strong>
                  <span>Set messages to go out on specific days and times that fit your strategy</span>
                </div>
              </div>
              <div className="v2-feature-list-item">
                <div className="v2-feature-list-dot" />
                <div>
                  <strong>Automated Campaigns</strong>
                  <span>Schedule SMS blasts in advance, ensuring consistent communication without manual effort</span>
                </div>
              </div>
              <div className="v2-feature-list-item">
                <div className="v2-feature-list-dot" />
                <div>
                  <strong>Targeted Delivery</strong>
                  <span>Align campaigns with customer time zones or peak engagement periods for maximum impact</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── ALERTS BANNER ── */}
      <section className="v2-banner v2-banner--alt" data-aos="fade-up">
        <div className="v2-banner-inner">
          <div className="v2-banner-text" data-aos="fade-right" data-aos-duration="1000">
            <span className="v2-section-label">Use Cases</span>
            <h2>Keep Your Customers Ahead with Instant SMS Alerts</h2>
            <p>Keep your audience informed with real-time SMS alerts and notifications. Whether it's important updates, critical alerts, or timely information, our platform ensures your messages reach the right people instantly, keeping them engaged and informed.</p>
            <a href="https://sms.dataflows.com.au/register" target="_blank" rel="noreferrer noopener" className="v2-btn-primary" onClick={handleGetStartedClick}>Get started</a>
          </div>
          <div className="v2-banner-image" data-aos="fade-left" data-aos-duration="1000">
            <img src="/mobile%20marketing-rafiki-1200w.png" alt="SMS Alerts" loading="lazy" />
          </div>
        </div>
      </section>

      {/* ── INTEGRATIONS ── */}
      <section className="v2-section v2-integrations-section" data-aos="fade-up">
        <div className="v2-section-inner">
          <div className="v2-section-header" data-aos="fade-up">
            <span className="v2-section-label">Integrations</span>
            <h2 className="v2-section-title">Integrated with the tools you know and love</h2>
            <p className="v2-section-subtitle">Easily synchronise with the tools you already use to streamline your workflow and maximise efficiency.</p>
          </div>
          <div className="v2-logo-carousel" data-aos="fade-up" data-aos-delay="100">
            <div className="v2-logo-track">
              {[...integrationLogos, ...integrationLogos].map((logo, i) => {
                const inner = (
                  <>
                    <img src={logo.src} alt={logo.alt} loading="lazy" />
                    <span className="v2-integration-logo-label">{logo.label}</span>
                  </>
                )
                if (logo.external) {
                  return <a key={i} href={logo.path} target="_blank" rel="noreferrer noopener" className="v2-integration-logo" aria-label={logo.label}>{inner}</a>
                }
                return logo.path
                  ? <Link key={i} to={logo.path} className="v2-integration-logo" aria-label={logo.label} onClick={() => window.scrollTo(0, 0)}>{inner}</Link>
                  : <div key={i} className="v2-integration-logo v2-integration-logo--soon">{inner}</div>
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="v2-section v2-how-section" data-aos="fade-up">
        <div className="v2-section-inner">
          <div className="v2-section-header" data-aos="fade-up">
            <span className="v2-section-label">How it works</span>
            <h2 className="v2-section-title">Get Started in 3 Simple Steps</h2>
          </div>
          <div className="v2-steps">
            <div className="v2-step" data-aos="fade-up" data-aos-delay="50">
              <div className="v2-step-content">
                <img src="/sign%20up-amico-1200w.png" alt="Sign up" loading="lazy" />
                <h3>Sign up</h3>
                <p>Create your account in minutes and get ready to connect with your audience.</p>
                <a href="https://sms.dataflows.com.au/register" target="_blank" rel="noreferrer noopener" className="v2-btn-primary" onClick={handleGetStartedClick}>Get started</a>
              </div>
            </div>
            <div className="v2-step" data-aos="fade-up" data-aos-delay="150">
              <div className="v2-step-content">
                <img src="/mobile%20login-pana-1200w.png" alt="Select Plan" loading="lazy" />
                <h3>Select a Plan</h3>
                <p>Choose the perfect plan tailored to your messaging needs and budget.</p>
              </div>
            </div>
            <div className="v2-step" data-aos="fade-up" data-aos-delay="250">
              <div className="v2-step-content">
                <img src="/logos/done-rafiki-1200w.png" alt="Start sending" loading="lazy" />
                <h3>Start Sending SMS</h3>
                <p>Begin sending personalised messages and track your results instantly.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── INTERACTIVE PHONE ── */}
      <PhoneMockup />

      {/* ── PRICING ── */}
      <section id="pricing" className="v2-section v2-pricing-section" data-aos="fade-up">
        <div className="v2-section-inner">
          <div className="v2-section-header" data-aos="fade-up">
            <span className="v2-section-label">Pricing</span>
            <h2 className="v2-section-title">Pricing for all kinds of businesses</h2>
            <p className="v2-section-subtitle">No monthly or annual fees — every plan is pay-as-you-go, so you only ever pay for the SMS credits you use.</p>
          </div>
          <div className="v2-pricing-grid">
            {pricingPlans.map((plan, i) => (
              <div key={i} className={`v2-pricing-card${plan.highlight ? ' v2-pricing-card--featured' : ''}`} data-aos="fade-up" data-aos-delay={100 + i * 80}>
                {plan.badge && <div className="v2-pricing-badge">{plan.badge}</div>}
                <div className="v2-pricing-name">{plan.name}</div>
                <div className="v2-pricing-price">
                  {plan.price}
                  {plan.period && <span className="v2-pricing-period"> {plan.period}</span>}
                </div>
                <div className="v2-pricing-rate">{plan.rate}</div>
                {plan.billingNote && <div className="v2-pricing-note">{plan.billingNote}</div>}
                <ul className="v2-pricing-features">
                  {plan.features.map((f, j) => (
                    <li key={j}>
                      <svg viewBox="0 0 20 20" fill="currentColor" className="v2-check"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href={plan.ctaHref}
                  target={plan.ctaHref.startsWith('http') ? '_blank' : undefined}
                  rel={plan.ctaHref.startsWith('http') ? 'noreferrer noopener' : undefined}
                  className={plan.highlight ? 'v2-btn-primary v2-btn-full' : 'v2-btn-outline v2-btn-full'}
                  onClick={plan.ctaHref.startsWith('http') ? handleGetStartedClick : undefined}
                >
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT FORM ── */}
      <div id="ContactForm41" className="v2-contact-wrapper">
        <ContactForm41 />
      </div>

      {/* ── RECENT BLOG POSTS ── */}
      <div className="v2-blog-wrapper">
        <RecentBlogPosts />
      </div>

      {/* ── ACTION BAR / CTA ── */}
      <section className="v2-cta-section" data-aos="fade-up">
        <div className="v2-cta-inner">
          <div className="v2-cta-glow" />
          <div className="v2-cta-text">
            <h2>Boost Your Reach with DataFlows!</h2>
            <p>Leverage the power of data to connect with your audience like never before.</p>
            <a href="https://sms.dataflows.com.au/register" target="_blank" rel="noreferrer noopener" className="v2-btn-primary v2-btn-lg" onClick={handleGetStartedClick}>
              Get Started Free
              <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18"><path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
            </a>
          </div>
          <div className="v2-cta-image">
            <img src="/launching-amico-1200w.png" alt="Launch with DataFlows" loading="lazy" />
          </div>
        </div>
      </section>

      {/* ── SEO CONTENT ── */}
      <section className="v2-seo-section">
        <div className="v2-seo-inner">
          <div className="v2-seo-block" data-aos="fade-up">
            <h2>SMS for Business Australia — Reliable Messaging Solutions</h2>
            <div className="v2-seo-grid">
              <div>
                <p>DataFlows is Australia's leading SMS platform designed specifically for businesses that need reliable, cost-effective SMS marketing and communication solutions. Our platform serves thousands of Australian companies with bulk SMS, automated messaging, and advanced SMS campaigns.</p>
                <p>Whether you're a small business in Melbourne, Sydney, Brisbane, or anywhere across Australia, our SMS business solutions help you connect with customers instantly. From appointment reminders to marketing campaigns, our Australian SMS service delivers messages with 98% open rates.</p>
              </div>
              <div>
                <h3>Why Australian Businesses Choose DataFlows SMS:</h3>
                <ul className="v2-seo-list">
                  <li>ACMA compliant SMS delivery across Australia</li>
                  <li>Local Australian phone numbers and sender IDs</li>
                  <li>24/7 Australian customer support</li>
                  <li>Integration with popular Australian business software</li>
                  <li>Competitive SMS pricing for Australian businesses</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="v2-seo-block" data-aos="fade-up">
            <h2>SMS API Australia — Developer-Friendly Integration</h2>
            <div className="v2-seo-grid">
              <div>
                <p>Our SMS API Australia service provides developers and businesses with powerful, easy-to-integrate SMS functionality. Built specifically for the Australian market, our SMS API supports all major Australian carriers and provides reliable delivery with comprehensive reporting.</p>
                <p>The DataFlows SMS API offers RESTful endpoints, webhook support, and comprehensive documentation. Perfect for Australian businesses needing to integrate SMS into their applications, websites, or existing business systems.</p>
              </div>
              <div>
                <h3>SMS API Features for Australian Developers:</h3>
                <ul className="v2-seo-list">
                  <li>RESTful SMS API with comprehensive documentation</li>
                  <li>Support for all major Australian carriers (Telstra, Optus, Vodafone)</li>
                  <li>Real-time delivery reports and webhook notifications</li>
                  <li>Bulk SMS API for high-volume messaging</li>
                  <li>Two-way SMS API for interactive messaging</li>
                  <li>SDK support for PHP, Python, Node.js, and more</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="v2-seo-block" data-aos="fade-up">
            <h2>SMS Solutions for Every Australian Industry</h2>
            <div className="v2-industry-grid">
              {[
                { title: 'Retail & E-commerce SMS', desc: 'Order confirmations, shipping updates, and promotional campaigns for Australian retailers.' },
                { title: 'Healthcare SMS Australia', desc: 'Appointment reminders, test results, and patient communications for Australian medical practices.' },
                { title: 'Real Estate SMS', desc: 'Property alerts, inspection reminders, and client communications for Australian real estate agents.' },
                { title: 'Automotive SMS', desc: 'Service reminders, booking confirmations, and customer updates for Australian automotive businesses.' },
              ].map((item, i) => (
                <div key={i} className="v2-industry-card">
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="v2-seo-block" data-aos="fade-up">
            <h2>Frequently Asked Questions</h2>
            <div className="v2-faq-grid">
              {[
                { q: 'How much does SMS for business cost in Australia?', a: 'DataFlows offers competitive SMS pricing starting from 2.5 cents per SMS on the Business plan. Our bulk SMS packages provide great value for high-volume messaging needs with zero subscription fees.' },
                { q: 'Is DataFlows SMS API compatible with Australian regulations?', a: 'Yes, our SMS API Australia platform is fully ACMA compliant and follows all Australian telecommunications regulations for business SMS messaging.' },
                { q: 'Can I use my business name as sender ID in Australia?', a: 'Absolutely! DataFlows provides approved business sender IDs for Australian companies on Pro and Business plans, helping you maintain brand consistency in your SMS communications.' },
                { q: 'How reliable is SMS delivery across Australia?', a: 'Our SMS platform delivers to all major Australian networks (Telstra, Optus, Vodafone) with 99.9% delivery reliability and detailed delivery reporting.' },
              ].map((item, i) => (
                <div key={i} className="v2-faq-item">
                  <h3>{item.q}</h3>
                  <p>{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <div className="v2-footer-wrapper">
        <Footer />
      </div>

      {/* ── SCROLL TO TOP ── */}
      {showScrollTop && (
        <button className="v2-scroll-top" onClick={scrollToTop} aria-label="Scroll to top">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 5l7 7h-4v7H9v-7H5l7-7z"/></svg>
        </button>
      )}
    </div>
  )
}

export default HomeV2
